import { EqualsIcon, ScalesIcon, TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { formatSignedPercent, marketNumbers, marketScalePosition } from './analytics';
import { formatCalendarDate, formatPrice } from './lib';
import type { MarketComparisonData } from './types';
import styles from './MarketPosition.module.css';

interface MarketPositionProps {
  market: MarketComparisonData | null;
}

/**
 * Цена против медианы — компактно, для карточки четвёрки (FE-595).
 *
 * Тот же расчёт, что в большом блоке страницы автомобиля (`MarketComparison`),
 * но в три строки: направление и процент, шкала с медианой посередине,
 * медиана и размер выборки. Цвет — синий «расчёт», а не зелёный «выгодно»:
 * цена ниже медианы сама по себе не преимущество (MVP_PRD §13, §16).
 *
 * Без полного набора чисел (медиана, выборка, дата снимка) блок говорит, что
 * сравнения нет и почему, — числа без основания не показываются.
 */
export function MarketPosition({ market }: MarketPositionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const numbers = market === null ? null : marketNumbers(market);

  if (market === null || numbers === null) {
    return (
      <section className={`${styles.block} ${styles.empty}`} aria-label={t('vehicle.market.title')}>
        <p className={styles.headline}>
          <ScalesIcon size={16} aria-hidden="true" />
          {market?.status === 'unavailable'
            ? t('vehicle.market.unavailable.title')
            : t('vehicle.market.insufficient.title')}
        </p>
      </section>
    );
  }

  const DirectionIcon =
    numbers.direction === 'below'
      ? TrendDownIcon
      : numbers.direction === 'above'
        ? TrendUpIcon
        : EqualsIcon;
  const snapshot = formatCalendarDate(numbers.snapshotDate, locale) ?? numbers.snapshotDate;

  return (
    <section className={styles.block} aria-label={t('vehicle.market.title')}>
      <p className={styles.headline}>
        <DirectionIcon size={16} weight="bold" aria-hidden="true" />
        <span className={`${styles.delta} tabular`}>
          {formatSignedPercent(numbers.deltaPercent, locale)}
        </span>
        <span>{t(`vehicle.market.direction.${numbers.direction}`)}</span>
      </p>

      {/* Шкала показывает направление и порядок отклонения, а не распределение
          цен: кроме медианы и размера выборки данных нет. */}
      <div className={styles.track} aria-hidden="true">
        <span className={styles.median} />
        <span
          className={styles.marker}
          style={
            {
              '--position': `${String(marketScalePosition(numbers.deltaPercent))}%`,
            } as React.CSSProperties
          }
        />
      </div>

      <p className={styles.meta}>
        {t('vehicle.market.median')}:{' '}
        <span className="tabular">{formatPrice(numbers.median, locale)}</span>
        {' · '}
        {t('vehicle.market.sampleValue', { count: numbers.sampleSize })}
      </p>

      {market.status === 'stale' ? (
        <p className={styles.stale}>{t('vehicle.market.stale', { date: snapshot })}</p>
      ) : null}
    </section>
  );
}
